import { useState, useCallback, useMemo } from 'react';
import { Button } from "react-bootstrap";
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import * as Yup from 'yup';

import ConfirmationPrompt from '../../controls/ConfirmationPrompt.jsx';
import Toolbar from '../../controls/Toolbar.jsx';
import { Form, Formik } from 'formik';
import NotificationTemplate from '../../../services/notificationTemplate.js';
import PubSub from '../../../services/PubSub.js';
import SendNotificationModal from './SendNotificationModal.jsx';
import { useHasPermission } from '../../../common/Hooks.jsx';
import Utils from '../../../utils/utils.js';
import NotificationTemplateLayout from './NotificationTemplateLayout.jsx';

const NotificationDetails = ({
  name,
  toastType,
  navigateTo,
  payload
}) => {
  const [translate] = useTranslation();
  const { id, objectSubTypeId } = useParams();

  const permissions = useHasPermission({
    domain: Utils.getDomain(),
    environment: null,
    deployment: id,
    type: 'notification',
  });

  const templatePermissions = useHasPermission({
    domain: Utils.getDomain(),
    environment: null,
    deployment: id,
    type: 'notificationTemplate',
  });

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [showSendNotification, setShowSendNotification] = useState(false);

  const deleteNotificationTemplate = useCallback(async () => {
    const response = await NotificationTemplate.deleteNotificationTemplate(id, objectSubTypeId);

    if (!response.error) {
      PubSub.publish('refresh-notifications', { entryDeleted: true });
    }
    setShowConfirmation(false);
  }, [id, objectSubTypeId]);

  const updateNotificationTemplate = useCallback(async (data) => {
    const payload = { ...data };
    delete payload.name;
    delete payload.toastType;
    delete payload.navigateTo;

    const response = await NotificationTemplate.updateNotificationTemplate(id, objectSubTypeId, { name: data.name, toastType: data.toastType, navigateTo: data.navigateTo, payload });
    if (!response.error) {
      setShowSaveButton(false);
      PubSub.publish('no-unsaved-changes');
      PubSub.publish('refresh-notifications', { entryDeleted: true });
    }
  }, [id, objectSubTypeId]);

  const onFormChange = useCallback(() => {
    if (!showSaveButton) {
      setShowSaveButton(true);
      PubSub.publish('has-unsaved-changes');
    }
  }, [showSaveButton]);

  const notificationDetailsForm = useMemo(() => {
    return <Formik
      initialValues={{
        name: name || '',
        toastType: toastType || '',
        navigateTo: navigateTo || '',
        title: payload?.title || '',
        badge: payload?.badge || '',
        body: payload?.body || '',
        icon: payload?.icon || '',
        image: payload?.image || '',
        renotify: payload?.renotify || false,
        silent: payload?.silent || false,
        requireInteraction: payload?.requireInteraction || false
      }}
      validationSchema={Yup.object({
        name: Yup.string().min(2, translate('OTM_CHARACTERS_OR_MORE', { minCharacters: 2 })).required(' ')
      })}
      onSubmit={updateNotificationTemplate}
      enableReinitialize>
      <Form onChange={onFormChange}>
        <div className='card-header border-bottom sticky-top bg-white'>
          <Toolbar
            buttons={[
              {
                name: 'Save',
                title: translate('OTM_SAVE'),
                type: 'submit',
                disabled: !templatePermissions.update,
                hidden: !showSaveButton
              },
              {
                name: 'DeleteOutlined',
                title: translate('OTM_DELETE'),
                onClick: () => setShowConfirmation(true),
                disabled: !templatePermissions.delete
              }
            ]}
            heading={translate('OTM_NOTIFICATION_TEMPLATE_DETAILS')}
          />
        </div>
        <div className='card-body'>
          <NotificationTemplateLayout
            disabled={!templatePermissions.update} />
          <Button
            className="col-12 mt-3"
            onClick={() => setShowSendNotification(true)}
          >
            {translate("OTM_NOTIFICATIONS_SEND")}
          </Button>
        </div>
      </Form>
    </Formik>
  }, [name, toastType, payload, templatePermissions, showSaveButton, navigateTo, onFormChange, updateNotificationTemplate, translate]);

  return (
    <>
      <ConfirmationPrompt show={showConfirmation} message={translate('OTM_DELETE_NOTIFICATION_TEMPLATE_?')}
        onConfirm={deleteNotificationTemplate} onCancel={() => setShowConfirmation(false)} />
      <SendNotificationModal show={showSendNotification} permissions={permissions} templateNotification={{ ...payload, name, toastType, navigateTo }} templatePermissions={templatePermissions} onClose={() => setShowSendNotification(false)}
      />
      <div className='card'>
        {notificationDetailsForm}
      </div>
    </>
  );
};

export default NotificationDetails;